import { z } from 'zod';
import { Slug } from './common.js';
import { FeatureKey, FeatureMap } from './features.js';
import { Role } from './roles.js';
export const TenantStatus = z.enum(['trial', 'active', 'suspended', 'pending_deletion']);
export const Workspace = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: Slug,
    status: TenantStatus,
    features: FeatureMap,
    /** Paid add-ons the platform has granted this workspace (docs/adr/0065); their switch shows in Features. */
    addOns: z.array(FeatureKey),
    /** Set while deletion is scheduled: the workspace is erased at this time unless an owner restores it. */
    deletionScheduledAt: z.string().nullable(),
});
export const Me = z.object({
    user: z.object({ id: z.uuid(), name: z.string(), email: z.email() }),
    role: Role,
    /**
     * A client guest whose whole app is Showcase (docs/adr/0068): Designs, Notifications and Account. The API serves
     * them nothing else, and their `workspace.features` is only `showcase`.
     */
    showcaseGuest: z.boolean().optional(),
    /**
     * A guest who sees only the conversations they were invited to (docs/adr/0085): Chat and Notifications. The API serves
     * them nothing else, and their `workspace.features` are all off (Showcase stays for someone who is also a Showcase guest).
     */
    conversationGuest: z.boolean().optional(),
    workspace: Workspace,
});
export const Member = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    name: z.string(),
    /**
     * Teammates: null when the person switched off "Show my email to teammates" (docs/adr/0047). The admin
     * view (owners and admins, Workspace settings > Members) always has it, with `emailHidden` saying so.
     */
    email: z.email().nullable(),
    /** As for email: null for teammates when not given or switched off; the admin view always has it. */
    phone: z.string().nullable(),
    /** Admin view only: the person keeps this from teammates. Null in the team view. */
    emailHidden: z.boolean().nullable(),
    phoneHidden: z.boolean().nullable(),
    role: Role,
    /**
     * 'invited' until the person signs in to this workspace for the first time; only joined owners, admins and
     * members are paid seats (docs/adr/0044).
     */
    status: z.enum(['invited', 'joined']),
    /** When they first signed in to the workspace; null while invited. */
    joinedAt: z.string().nullable(),
    /** When the membership (the invitation) was made. */
    invitedAt: z.string(),
    /** When the invitation email was last sent; null for people added without one (an import). */
    inviteSentAt: z.string().nullable(),
    /** The identity provider deactivated them (SCIM, docs/adr/0055): no access, not a seat. */
    suspended: z.boolean().optional(),
    /** Provisioned by the identity provider (SCIM): it owns the name, email and active state. */
    scimManaged: z.boolean().optional(),
});
/** `admin`: Workspace settings > Members, owners and admins only (404 otherwise). `team`: everyone else's view. */
export const MemberListQuery = z.object({ view: z.enum(['team', 'admin']).default('team') });
export const MemberList = z.object({ items: z.array(Member) });
export const ChangeRoleInput = z.object({ role: Role });
export const InviteMemberInput = z.object({
    email: z.email().max(254),
    name: z.string().trim().min(1).max(80),
    role: Role,
    /** Guests: the channels they may see. Team members: client or project channels to add them to, besides every public team channel (docs/adr/0074). */
    channelIds: z.array(z.uuid()).max(50).optional(),
});
export const UpdateWorkspaceInput = z.object({ name: z.string().trim().min(2).max(80) });
//# sourceMappingURL=tenants.js.map